import { useEffect, useState, Suspense } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { App } from "antd";
import Loading from "../components/loading/Loading";
import { socket } from "../components/socket/Sockets";
import { URL } from "../constant/url";
import Login from "../page/login/Login";
import LocationPage from "../page/location/LocationPage";
import MainLayout from "../page/main/MainLayout";
import Menu from "../subpage/menu/Menu";
import SaveBill from "../subpage/savebill/SaveBill";
import NewBill from "../subpage/newbill/NewBill";
import WorkingShift from "../subpage/workingshift/WorkingShift";
import CompletedOrder from "../subpage/completedorder/CompletedOrder";
import { useStore } from "../components/globalstate/GlobalState";

/* 
-------------------------------------------
Reusable Protected Route Component
-------------------------------------------
*/
const ProtectedRoute = ({ children }) => {
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const { message } = App.useApp();

  useEffect(() => {
    const verifyToken = async () => {
      const token = localStorage.getItem("token");
      if (!token) {
        setIsAuthenticated(false);
        setLoading(false);
        return;
      }

      try {
        const res = await fetch(`${URL}/api/refresh-token`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ token }),
        });

        const data = await res.json();

        if (res.ok && data.token) {
          localStorage.setItem("token", data.token);
          setIsAuthenticated(true);
        } else {
          message.error("Token expired");
          localStorage.removeItem("token");
        }
      } catch (err) {
        console.error("Token check error:", err);
        localStorage.removeItem("token");
      } finally {
        setLoading(false);
      }
    };

    verifyToken();
  }, [message]);

  if (loading) return <Loading />;
  return isAuthenticated ? children : <Navigate to="/login" replace />;
};

/* 
-------------------------------------------
Reusable Hook for Socket Event Handling
-------------------------------------------
*/
const useSocketChannel = (eventName, joinEvent) => {
  const location = useStore((state) => state.location);
  const [dataList, setDataList] = useState([]);
  const LOCATION = localStorage.getItem("selectedLocation") || location;

  useEffect(() => {
    socket.emit(joinEvent, LOCATION);

    socket.emit('online',{
      user: localStorage.getItem("cashier"),
      location: LOCATION
    })

    const handleEvent = (data) => {
      if (Array.isArray(data)) {
        setDataList(data);
      } else {
        setDataList((prev) => [...prev, data]);
      }
    };

    socket.on(eventName, handleEvent);

    return () => {
      socket.off(eventName, handleEvent);
    };
  }, [eventName, joinEvent, LOCATION]);

  return dataList;
};

/* 
-------------------------------------------
Main Router Component
-------------------------------------------
*/
const Routers = () => {
  const saveBills = useSocketChannel("save-bill", "join-save-bill");
  const completedBills = useSocketChannel("store-bill", "join-store-bill");
  const newBills = useSocketChannel("new-bill", "join-new-bill");

  return (
    <Suspense fallback={<Loading />}>
      <Router>
        <Routes>
          {/* Public */}
          <Route path="/login" element={<Login />} />

          {/* Test route (unprotected) */}
          <Route
            path="/test"
            element={<MainLayout listSaveBills={saveBills} />}
          />

          {/* Protected Routes */}

          <Route
            path="/locations"
            element={
              <ProtectedRoute>
                <LocationPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/order-processing"
            element={
              <ProtectedRoute>
                <MainLayout
                  listBills={saveBills}
                  ListOfCompletedBills={completedBills}
                  newBill={newBills}
                />
              </ProtectedRoute>
            }
          >
            <Route index element={<Navigate to="menu" replace />} />
            <Route path="menu" element={<Menu />} />
            <Route path="saved" element={<SaveBill ListOfSaveBill={saveBills} />} />
            <Route path="new" element={<NewBill newBill={newBills} />} />
            <Route path="calendar" element={<WorkingShift />} />
            <Route
              path="completed"
              element={<CompletedOrder ListOfCompletedBills={completedBills} />}
            />
          </Route>

          {/* Fallback */}
          <Route path="*" element={<Navigate to="/order-processing" replace />} />
        </Routes>
      </Router>
    </Suspense>
  );
};

export default Routers;
